/**
 * Getallen en datums in de taal van de lezer — en verder niets.
 *
 * `maximumFractionDigits: 20` is geen overdrijving maar de hele bedoeling: `Intl` rondt standaard
 * af op drie decimalen, en afronden is rekenen. De dienst levert elke waarde al afgerond (AD-19);
 * wat hier gebeurt is uitsluitend het scheidingsteken kiezen.
 */

/**
 * True zodra `waarde` een getal is waar iets mee te tonen valt.
 *
 * `!== undefined` is hiervoor niet genoeg, en dat is geen muggenzifterij: JSON kent `null`, en
 * `formatGetal(null)` levert via `Intl` doodleuk "0". Precies de nul die AD-19 verbiedt — een nul
 * die niet gemeten is maar uit afwezigheid ontstaat, en die op dit scherm als "nul correcties"
 * leest. `NaN` en `Infinity` gaan om dezelfde reden mee: die komen als "NaN" en "∞" in beeld.
 */
export function isGetal(waarde: unknown): waarde is number {
  return typeof waarde === 'number' && Number.isFinite(waarde);
}

/**
 * True zodra `waarde` een telling is: een getal dat niet negatief is.
 *
 * "-3 keer leeg gelaten" en "-3 producten" zijn geen meetuitkomsten maar een fout in het antwoord,
 * en die hoort als zodanig op het scherm te komen — met de reden ernaast — in plaats van als een
 * cijfer over een mens. Dezelfde weigering als de grenscontrole 0–100 doet voor een percentage.
 */
export function isTelling(waarde: unknown): waarde is number {
  return isGetal(waarde) && waarde >= 0;
}

/**
 * True voor een gewoon object uit een antwoord: geen `null`, geen lijst, geen tekst.
 *
 * Een antwoord dat een blok als `"kapot"` of `[]` stuurt, noemt dat blok niet; wie er dan toch in
 * leest, laat het scherm werpen (story 12, Robuustheid).
 */
export function isObject(waarde: unknown): waarde is Record<string, unknown> {
  return typeof waarde === 'object' && waarde !== null && !Array.isArray(waarde);
}

export function formatGetal(waarde: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 20 }).format(waarde);
}

/**
 * Een ISO-tijdstempel als dag, maand en jaar in de taal van de lezer. In UTC, zoals de meting.
 *
 * Een tijdstempel die geen datum is komt onbewerkt terug in plaats van als "Invalid Date": dan is
 * op het scherm te zien wát er binnenkwam, in plaats van een Engelse foutmelding die als tekst leest.
 */
export function formatDatum(iso: string, locale: string): string {
  const datum = new Date(iso);
  if (Number.isNaN(datum.getTime())) return iso;
  return datum.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Hetzelfde tijdstempel, met de weekdag erbij — voor een moment dat nog moet komen.
 *
 * "maandag 7 september" zegt een lezer meer over een gepland moment dan "7 september", en het sluit
 * aan op het ritme dat deze module beschrijft. Het uur staat er met opzet **niet** bij: de meting
 * rekent in UTC en dit scherm toont die dag ook in UTC, dus een klokstand zou er twee uur naast
 * kunnen staan voor wie hem afleest. Een dag is wat er is toegezegd; een tijdstip niet.
 */
export function formatDagDatum(iso: string, locale: string): string {
  const datum = new Date(iso);
  if (Number.isNaN(datum.getTime())) return iso;
  return datum.toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
